import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  MessageSquare,
  Sparkles,
  Send,
  Wand2,
  Trash2,
  RefreshCw,
  Users,
  Search,
  Copy,
  ScanLine,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const TIER_LABELS = {
  free: "Free",
  vip: "VIP",
  both: "Free + VIP",
};

const AdminCaptionsBoveda = () => {
  const { getAuthHeaders } = useAuth();

  const [vault, setVault] = useState({
    captions: [],
    totalUnique: 0,
    totalRecords: 0,
    activeCreators: [],
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [bulkText, setBulkText] = useState("");
  const [tier, setTier] = useState("both");
  const [mode, setMode] = useState("all");
  const [stripLinks, setStripLinks] = useState(true);
  const [selectedCreators, setSelectedCreators] = useState([]);
  const [distributing, setDistributing] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [propagating, setPropagating] = useState(false);
  const [dupes, setDupes] = useState(null); // {groups, totalGroups, removableRecords} | null
  const [dupeOpen, setDupeOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [selectedTexts, setSelectedTexts] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/autopost/captions/vault`, {
        headers: { ...getAuthHeaders() },
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        toast.error("Función no disponible aún. Haz Deploy.");
        setVault({ captions: [], totalUnique: 0, totalRecords: 0, activeCreators: [] });
        return;
      }
      const d = await r.json();
      setVault(d);
    } catch {
      toast.error("Error al cargar la bóveda");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const distribute = async () => {
    const lines = bulkText
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) {
      toast.error("Pega al menos un caption por línea");
      return;
    }
    if (vault.activeCreators.length === 0) {
      toast.error("No hay modelos activas");
      return;
    }
    setDistributing(true);
    try {
      const r = await fetch(`/api/admin/autopost/captions/distribute`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          texts: lines,
          tier,
          mode,
          stripLinks,
          usernames: selectedCreators.length ? selectedCreators : null,
        }),
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        toast.error("Función no disponible. Haz Deploy.");
        return;
      }
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error al distribuir");
        return;
      }
      toast.success(
        `✅ ${d.inserted} captions agregados a ${d.creators} modelos · ⏭ ${d.skippedDuplicates} duplicados`,
        { duration: 6000 }
      );
      setBulkText("");
      load();
    } catch {
      toast.error("Error de red");
    } finally {
      setDistributing(false);
    }
  };

  const consolidate = async () => {
    if (
      !window.confirm(
        "Esto recorrerá TODOS los captions ya guardados, quitará los links y borrará los que queden vacíos. ¿Continuar?"
      )
    )
      return;
    setConsolidating(true);
    try {
      const r = await fetch(`/api/admin/autopost/captions/consolidate`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error");
        return;
      }
      toast.success(
        `✨ ${d.linksStripped} captions limpiados · 🗑 ${d.deletedEmpty} vacíos eliminados (de ${d.scanned} totales)`
      );
      load();
    } catch {
      toast.error("Error de red");
    } finally {
      setConsolidating(false);
    }
  };

  const propagateToAll = async () => {
    if (
      !window.confirm(
        "Esto BORRARÁ el pool actual y REPARTIRÁ todas las captions únicas entre las modelos activas (round-robin: cada caption queda en UNA sola modelo). ¿Continuar?"
      )
    )
      return;
    setPropagating(true);
    try {
      const r = await fetch(`/api/admin/autopost/captions/propagate-to-all`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error");
        return;
      }
      toast.success(
        `🔁 ${d.uniqueTexts} captions repartidos entre ${d.creators} modelos`,
        {
          description: Object.entries(d.perCreator || {})
            .map(([u, n]) => `@${u}: ${n}`)
            .join(" · "),
          duration: 8000,
        }
      );
      load();
    } catch {
      toast.error("Error de red");
    } finally {
      setPropagating(false);
    }
  };

  const scanDuplicates = async () => {
    setScanning(true);
    try {
      const r = await fetch(`/api/admin/autopost/captions/duplicates`, {
        headers: { ...getAuthHeaders() },
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error");
        return;
      }
      setDupes(d);
      setDupeOpen(true);
      if (d.totalGroups === 0) {
        toast.success("✅ No hay duplicados");
      } else {
        toast.message(
          `🔍 ${d.totalGroups} grupo(s) con duplicados · ${d.removableRecords} registros eliminables`
        );
      }
    } catch {
      toast.error("Error de red");
    } finally {
      setScanning(false);
    }
  };

  const dedupeAll = async () => {
    if (
      !window.confirm(
        `Se mantendrá 1 registro por grupo (el de tier "Free + VIP" si existe, o el más antiguo) y se borrarán los demás. ¿Continuar?`
      )
    )
      return;
    setDeduping(true);
    try {
      const r = await fetch(`/api/admin/autopost/captions/dedupe`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error");
        return;
      }
      toast.success(`🗑 ${d.deleted} duplicados borrados · ✓ ${d.kept} grupos limpiados`);
      setDupes(null);
      setDupeOpen(false);
      load();
    } catch {
      toast.error("Error de red");
    } finally {
      setDeduping(false);
    }
  };

  const deleteByText = async (text) => {
    if (!window.confirm(`Eliminar "${text.slice(0, 60)}…" de TODAS las modelos?`)) return;
    try {
      const r = await fetch(
        `/api/admin/autopost/captions/delete-by-text?text=${encodeURIComponent(text)}`,
        { method: "DELETE", headers: { ...getAuthHeaders() } }
      );
      const d = await r.json();
      toast.success(`🗑 ${d.deleted || 0} eliminados`);
      load();
    } catch {
      toast.error("Error");
    }
  };

  const toggleSelected = (text) => {
    setSelectedTexts((prev) => {
      const next = new Set(prev);
      if (next.has(text)) next.delete(text); else next.add(text);
      return next;
    });
  };

  const selectAllVisible = () => {
    const allVisible = filtered.map((c) => c.text);
    setSelectedTexts((prev) => {
      const next = new Set(prev);
      const allSelected = allVisible.every((t) => next.has(t));
      if (allSelected) {
        allVisible.forEach((t) => next.delete(t));
      } else {
        allVisible.forEach((t) => next.add(t));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedTexts(new Set());

  const deleteSelected = async () => {
    if (selectedTexts.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedTexts.size} caption(s) de TODAS las modelos? Esta acción no se puede deshacer.`)) return;
    setBulkDeleting(true);
    try {
      const r = await fetch(`/api/admin/autopost/captions/delete-bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ texts: Array.from(selectedTexts) }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`🗑 ${d.deleted || 0} caption(s) eliminados`);
        clearSelection();
        load();
      } else {
        toast.error(d.detail || "Error al eliminar");
      }
    } catch {
      toast.error("Error de red");
    } finally {
      setBulkDeleting(false);
    }
  };

  const deleteAll = async () => {
    if (vault.totalRecords === 0) return;
    const confirm1 = window.confirm(
      `⚠️ Vas a eliminar TODOS los captions (${vault.totalUnique} únicos · ${vault.totalRecords} registros). ¿Estás seguro?`
    );
    if (!confirm1) return;
    const confirm2 = window.prompt('Esta acción es IRREVERSIBLE. Escribe "BORRAR TODO" para confirmar:');
    if (confirm2 !== "BORRAR TODO") {
      toast("Cancelado");
      return;
    }
    setBulkDeleting(true);
    try {
      const r = await fetch(`/api/admin/autopost/captions/clear-all`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`🗑 ${d.deleted || 0} captions eliminados`);
        clearSelection();
        load();
      } else {
        toast.error(d.detail || "Error");
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleCreator = (u) => {
    setSelectedCreators((prev) =>
      prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u]
    );
  };

  const filtered = (vault.captions || []).filter((c) =>
    !search ? true : (c.text || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-pink-400" />
              Bóveda de Captions
            </h1>
            <p className="text-white/60 text-sm">
              Pega muchos captions y se reparten entre todas las modelos activas. Los links se eliminan automáticamente.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={propagateToAll}
              disabled={propagating}
              data-testid="propagate-all-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-200 rounded-lg text-sm font-medium disabled:opacity-50"
              title="Borra el pool actual y reparte las captions únicas entre las modelos (cada caption en UNA sola modelo)"
            >
              <Copy className="w-4 h-4" />
              {propagating ? "Repartiendo…" : "Repartir entre todas"}
            </button>
            <button
              onClick={scanDuplicates}
              disabled={scanning}
              data-testid="scan-duplicates-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/15 hover:bg-pink-500/25 border border-pink-500/30 text-pink-200 rounded-lg text-sm font-medium disabled:opacity-50"
              title="Buscar registros duplicados dentro de cada modelo"
            >
              <ScanLine className="w-4 h-4" />
              {scanning ? "Escaneando…" : "Buscar duplicados"}
            </button>
            <button
              onClick={consolidate}
              disabled={consolidating}
              data-testid="consolidate-existing-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-200 rounded-lg text-sm font-medium disabled:opacity-50"
              title="Quita links de captions ya guardados y elimina vacíos"
            >
              <Wand2 className="w-4 h-4" />
              {consolidating ? "Limpiando…" : "Limpiar links"}
            </button>
          </div>
        </div>

        {/* Duplicates panel */}
        {dupeOpen && dupes && (
          <div className="bg-pink-500/5 border border-pink-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-pink-300" />
                <h3 className="text-white font-bold text-sm">
                  Duplicados detectados:{" "}
                  <span className="text-pink-300">{dupes.totalGroups}</span> grupo(s) ·{" "}
                  <span className="text-pink-300">{dupes.removableRecords}</span> registros eliminables
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {dupes.totalGroups > 0 && (
                  <button
                    onClick={dedupeAll}
                    disabled={deduping}
                    data-testid="dedupe-all-btn"
                    className="px-3 py-1.5 bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                  >
                    {deduping ? "Borrando…" : `Borrar ${dupes.removableRecords} duplicados`}
                  </button>
                )}
                <button
                  onClick={() => setDupeOpen(false)}
                  className="text-white/50 hover:text-white text-xs px-2 py-1.5"
                >
                  Cerrar
                </button>
              </div>
            </div>
            {dupes.totalGroups === 0 ? (
              <p className="text-white/60 text-xs">
                ✅ Todo limpio. No hay registros duplicados.
              </p>
            ) : (
              <div className="space-y-2 max-h-[320px] overflow-y-auto">
                {dupes.groups.slice(0, 50).map((g, idx) => (
                  <div
                    key={`${g.creatorUsername}-${idx}`}
                    className="bg-black/30 border border-white/5 rounded-lg p-3 text-xs"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white/80 font-medium">@{g.creatorUsername}</span>
                      <span className="text-pink-300 text-[11px]">
                        x{g.count} copias
                      </span>
                    </div>
                    <p className="text-white/70 truncate" title={g.text}>
                      {g.text}
                    </p>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {g.items.map((it) => (
                        <span
                          key={it.id}
                          className="text-[10px] px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-white/50"
                        >
                          {it.tier}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                {dupes.groups.length > 50 && (
                  <p className="text-white/40 text-[11px] text-center pt-2">
                    +{dupes.groups.length - 50} grupos más
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <p className="text-[11px] text-white/50 uppercase tracking-wider">Únicos</p>
            <p className="text-2xl font-bold text-white" data-testid="vault-total-unique">{vault.totalUnique}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <p className="text-[11px] text-white/50 uppercase tracking-wider">Registros</p>
            <p className="text-2xl font-bold text-white">{vault.totalRecords}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <p className="text-[11px] text-white/50 uppercase tracking-wider">Modelos activas</p>
            <p className="text-2xl font-bold text-white">{vault.activeCreators.length}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Editor */}
          <div className="lg:col-span-3 space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4">
              <div>
                <label className="text-xs text-white/60 block mb-1.5">
                  Captions (uno por línea)
                </label>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={12}
                  placeholder={`Pega tus captions, uno por línea. Por ejemplo:\n\n¿Listo para verme?\nNuevo contenido subido 🔥\nVen a mi canal y descubre lo nuevo 💋`}
                  data-testid="captions-bulk-textarea"
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30 resize-y font-mono"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  {bulkText.split("\n").filter((l) => l.trim()).length} línea(s) detectada(s)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60 block mb-1.5">Tier</label>
                  <select
                    value={tier}
                    onChange={(e) => setTier(e.target.value)}
                    data-testid="captions-tier-select"
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30"
                  >
                    <option value="both">Free + VIP</option>
                    <option value="free">Solo Free</option>
                    <option value="vip">Solo VIP</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/60 block mb-1.5">Modo</label>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    data-testid="captions-mode-select"
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30"
                  >
                    <option value="all">Todos a todas (cada modelo recibe todos)</option>
                    <option value="roundrobin">Repartir 1 a 1 (round-robin)</option>
                  </select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                <input
                  type="checkbox"
                  checked={stripLinks}
                  onChange={(e) => setStripLinks(e.target.checked)}
                  data-testid="captions-strip-links"
                  className="accent-pink-500"
                />
                Eliminar links automáticamente (URLs, t.me, onlyfans, bit.ly…)
              </label>

              <button
                onClick={distribute}
                disabled={distributing || !bulkText.trim()}
                data-testid="captions-distribute-btn"
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                {distributing
                  ? "Distribuyendo…"
                  : `Distribuir a ${
                      selectedCreators.length || vault.activeCreators.length
                    } modelo(s)`}
              </button>
            </div>
          </div>

          {/* Creators selector */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-bold text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Destinatarias
                </h2>
                <button
                  onClick={() => setSelectedCreators([])}
                  className="text-[11px] text-white/50 hover:text-white"
                >
                  Todas
                </button>
              </div>
              <p className="text-[11px] text-white/40 mb-2">
                Si no marcas ninguna, se envía a TODAS las modelos activas.
              </p>
              <div className="space-y-1 max-h-[280px] overflow-y-auto pr-1">
                {vault.activeCreators.map((c) => (
                  <label
                    key={c.username}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCreators.includes(c.username)}
                      onChange={() => toggleCreator(c.username)}
                      className="accent-pink-500"
                    />
                    <span className="text-white/80 truncate">
                      @{c.username}
                      {c.name ? <span className="text-white/40"> · {c.name}</span> : null}
                    </span>
                  </label>
                ))}
                {vault.activeCreators.length === 0 && (
                  <p className="text-white/40 text-xs text-center py-4">
                    Sin modelos activas
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Vault list */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-white font-bold flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Captions en bóveda
              <span className="text-white/40 text-sm font-normal">
                ({vault.totalUnique} únicos · {vault.totalRecords} registros)
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar…"
                  data-testid="captions-search"
                  className="bg-black/30 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-white text-xs outline-none focus:border-white/30 w-44"
                />
              </div>
              <button onClick={load} className="p-1.5 text-white/50 hover:text-white">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Bulk action toolbar */}
          {filtered.length > 0 && (
            <div className="mb-3 flex items-center justify-between flex-wrap gap-2 px-3 py-2 bg-black/30 border border-white/10 rounded-lg">
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((c) => selectedTexts.has(c.text))}
                    onChange={selectAllVisible}
                    className="accent-pink-500"
                    data-testid="captions-select-all"
                  />
                  Seleccionar todo ({filtered.length})
                </label>
                {selectedTexts.size > 0 && (
                  <span className="text-xs text-pink-300 font-semibold ml-2">
                    {selectedTexts.size} seleccionado(s)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedTexts.size > 0 && (
                  <>
                    <button
                      onClick={clearSelection}
                      className="text-xs text-white/50 hover:text-white px-2 py-1"
                    >
                      Limpiar
                    </button>
                    <button
                      onClick={deleteSelected}
                      disabled={bulkDeleting}
                      data-testid="captions-delete-selected"
                      className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/40 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 font-bold disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Eliminar seleccionados
                    </button>
                  </>
                )}
                <button
                  onClick={deleteAll}
                  disabled={bulkDeleting || vault.totalRecords === 0}
                  data-testid="captions-delete-all"
                  className="text-xs bg-red-600/15 hover:bg-red-600/25 text-red-300 border border-red-600/40 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 font-bold disabled:opacity-50"
                  title="Eliminar absolutamente todos los captions"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Borrar todo
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <p className="text-white/40 text-center py-8 text-sm">Cargando…</p>
          ) : filtered.length === 0 ? (
            <p className="text-white/40 text-center py-8 text-sm">
              {vault.totalUnique === 0
                ? "La bóveda está vacía. Pega tus primeros captions arriba 👆"
                : "Sin resultados para esta búsqueda"}
            </p>
          ) : (
            <div className="space-y-2">
              {filtered.map((c) => (
                <div
                  key={c.text}
                  className={`flex items-start gap-3 p-3 rounded-lg border group ${
                    selectedTexts.has(c.text)
                      ? "bg-pink-500/5 border-pink-500/40"
                      : "bg-black/20 border-white/5 hover:border-white/10"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedTexts.has(c.text)}
                    onChange={() => toggleSelected(c.text)}
                    className="accent-pink-500 mt-1 shrink-0"
                    data-testid={`captions-check-${c.text.slice(0, 12)}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm whitespace-pre-wrap break-words">{c.text}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {c.tiers.map((t) => (
                        <span
                          key={t}
                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                            t === "vip"
                              ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                              : t === "free"
                              ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                              : "bg-white/10 text-white/70 border border-white/15"
                          }`}
                        >
                          {TIER_LABELS[t] || t}
                        </span>
                      ))}
                      <span className="text-[10px] text-white/40">
                        En {c.count} modelo(s):{" "}
                        {c.creators
                          .slice(0, 3)
                          .map((cr) => "@" + cr.username)
                          .join(", ")}
                        {c.creators.length > 3 && ` +${c.creators.length - 3}`}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteByText(c.text)}
                    data-testid={`delete-caption-${c.text.slice(0, 12)}`}
                    className="opacity-50 group-hover:opacity-100 text-red-400/70 hover:text-red-400 p-1"
                    title="Eliminar de TODAS las modelos"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCaptionsBoveda;
